import { useEffect, useState, RefObject } from "react";

interface Position {
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  right?: string | number;
  transform?: string;
  maxHeight?: string;
}

interface UseDropdownPositionOptions {
  isOpen: boolean;
  buttonRef: RefObject<HTMLElement>;
  menuRef: RefObject<HTMLElement>;
  preferredPosition?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  offset?: number;
}

export const useDropdownPosition = ({
  isOpen,
  buttonRef,
  menuRef,
  preferredPosition = "bottom-right",
  offset = 4,
}: UseDropdownPositionOptions): Position => {
  const [position, setPosition] = useState<Position>({});

  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      setPosition({});
      return;
    }

    const calculatePosition = () => {
      if (!buttonRef.current) {
        // Sem botão, usar posição padrão
        setPosition({});
        return;
      }

      // Se o menu ainda não foi renderizado, retornar posição vazia (usa padrão CSS)
      // Isso permite que o menu seja renderizado primeiro
      if (!menuRef.current) {
        setPosition({});
        return;
      }

      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const newPosition: Position = {};

      // Calcular posição horizontal - sempre permitir renderização
      const spaceRight = viewportWidth - buttonRect.right;
      const spaceLeft = buttonRect.left;
      const menuWidth = menuRect.width || 256; // w-64 = 256px

      // Por padrão, usar posicionamento relativo (right-0 via className)
      // Só ajustar se realmente necessário (quando não cabe no espaço preferido)
      if (preferredPosition.includes("right")) {
        if (
          spaceRight < menuWidth + offset &&
          spaceLeft >= menuWidth + offset
        ) {
          // Não cabe à direita, mas cabe à esquerda
          newPosition.left = 0;
          newPosition.transform = "translateX(-100%)";
        } else if (spaceRight < menuWidth + offset) {
          // Não cabe à direita nem à esquerda - ajustar para caber na tela
          // Garantir que pelo menos parte do menu fique visível
          const maxRight = viewportWidth - offset;
          newPosition.right = Math.max(0, maxRight - buttonRect.right);
        }
        // Caso contrário, usa right-0 via className (sempre funciona)
      } else {
        // preferredPosition.includes('left')
        if (
          spaceLeft < menuWidth + offset &&
          spaceRight >= menuWidth + offset
        ) {
          // Não cabe à esquerda, mas cabe à direita
          newPosition.right = 0;
        } else if (spaceLeft < menuWidth + offset) {
          // Não cabe à esquerda nem à direita - ajustar para caber na tela
          // Garantir que pelo menos parte do menu fique visível
          const maxLeft = offset;
          newPosition.left = Math.max(0, buttonRect.left - maxLeft);
        }
        // Caso contrário, usa left-0 via className (sempre funciona)
      }

      // Calcular posição vertical - abrir para cima quando há mais espaço acima ou pouco espaço abaixo
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const menuHeight = Math.max(menuRect.height || 0, 320);

      if (preferredPosition.includes("bottom")) {
        // Preferir abrir para cima quando: há mais espaço acima OU não cabe o menu inteiro abaixo
        const preferAbove =
          spaceAbove >= spaceBelow || spaceBelow < menuHeight + offset;
        if (preferAbove) {
          newPosition.bottom = `calc(100% + ${offset}px)`;
          newPosition.top = "auto";
          if (spaceAbove < menuHeight + offset) {
            newPosition.maxHeight = `${Math.max(spaceAbove - offset, 150)}px`;
          }
        } else if (spaceBelow < menuHeight + offset) {
          // Abre abaixo mas com pouco espaço: limitar altura para caber na tela
          newPosition.maxHeight = `${Math.max(spaceBelow - offset, 150)}px`;
        }
      } else {
        // preferredPosition.includes('top')
        // Sempre tentar mostrar acima primeiro
        if (spaceAbove >= menuHeight + offset) {
          // Cabe perfeitamente acima - usa bottom-full via className
          // Não precisa adicionar nada ao style
        } else if (spaceBelow >= menuHeight + offset) {
          // Não cabe acima, mas cabe abaixo - mostrar abaixo
          newPosition.top = `calc(100% + ${offset}px)`;
        } else {
          // Não cabe nem acima nem abaixo - mostrar acima mas limitar altura
          // SEMPRE mostrar, mesmo que cortado
          newPosition.maxHeight = `${Math.max(spaceAbove - offset, 150)}px`;
        }
      }

      setPosition(newPosition);
    };

    // Calcular imediatamente se o menu já existe
    if (menuRef.current) {
      calculatePosition();
    }

    // Recalcular após um pequeno delay para garantir que o menu foi renderizado
    const timeoutId = setTimeout(() => {
      calculatePosition();
    }, 10);

    // Recalcular em redimensionamento e scroll
    window.addEventListener("resize", calculatePosition);
    window.addEventListener("scroll", calculatePosition, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition, true);
    };
  }, [isOpen, buttonRef, menuRef, preferredPosition, offset]);

  return position;
};
